import { computed } from 'vue'
import type { Ref } from 'vue'
import { Normalization } from '~/utils/normalization'
import { AppProblemModel } from '~/services/api/AppProblemModel'
import { resolveProblemMessage } from '~/services/api/resolveProblemMessage'
import { serializeAppProblem } from '~/services/api/serializeAppProblem'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import type {
  SearchCategoriesSuccess,
  SearchDietaryOptionsSuccess,
  SearchTagsSuccess
} from '~/services/api/generated'

// Global app data fetcher to prevent multiple calls
interface AppCategory {
  id: string | number
  name?: string
  slug?: string
  type?: string
  isSelected: boolean
  [key: string]: unknown
}
interface DietaryOption {
  id: string | number
  slug: string | number
  label: string
  value: null
}
type CategoryResponse = SearchCategoriesSuccess
type DietaryResponse = SearchDietaryOptionsSuccess
type TagsResponse = SearchTagsSuccess
let globalFetchPromise: Promise<void> | null = null

const isRecord = Normalization.isRecord

const normalizeId = (value: unknown): string | number => {
  if (typeof value === 'string' || typeof value === 'number') return value
  return Normalization.stringValue(value, '') ?? ''
}

interface NormalizedCategory {
  id: string | number
  name?: string
  slug?: string
  type?: string
  [key: string]: unknown
}

const normalizeCategory = (value: unknown): NormalizedCategory => {
  const source = isRecord(value) ? value : {}
  const { name: _name, slug: _slug, type: _type, ...rest } = source
  const id = normalizeId(source['id'])
  const name = Normalization.trimmedStringValue(source['name'])
  const slug = Normalization.trimmedStringValue(source['slug'])
  const type = Normalization.trimmedStringValue(source['type'])

  return {
    ...rest,
    id,
    ...(name !== null ? { name } : {}),
    ...(slug !== null ? { slug } : {}),
    ...(type !== null ? { type } : {})
  }
}

const normalizeSlug = (rawSlug: unknown, fallbackId: string | number): string | number => {
  if (typeof rawSlug === 'string' && rawSlug.length > 0) return rawSlug
  if (typeof rawSlug === 'number') return rawSlug
  return fallbackId
}

const normalizeDietaryOption = (value: unknown): DietaryOption => {
  const source = isRecord(value) ? value : {}
  const id = normalizeId(source['id'])
  return {
    id,
    slug: normalizeSlug(source['slug'], id),
    label: Normalization.stringValue(source['name'], '') ?? '',
    value: null
  }
}

const fetchAppDataGlobal = async (
  categories: Ref<AppCategory[]>,
  dietaryOptions: Ref<DietaryOption[]>,
  tags: Ref<Record<string, unknown>>,
  tagGroups: Ref<string[]>,
  loading: Ref<boolean>,
  failure: Ref<AppProblemModel | null>
): Promise<void> => {
  if (globalFetchPromise) {
    return globalFetchPromise
  }

  globalFetchPromise = (async () => {
    if (import.meta.server) {
      return
    }

    loading.value = true
    failure.value = null

    try {
      const { apiBaseUrl: baseUrl } = useRuntimeConfig().public

      if (!baseUrl) {
        throw new Error('NUXT_PUBLIC_API_BASE_URL is not set — see .env.example')
      }

      const [categoriesResponse, dietaryResponse, tagsResponse] = await Promise.all([
        $fetch<CategoryResponse>(`${baseUrl}/api/search/categories`),
        $fetch<DietaryResponse>(`${baseUrl}/api/search/dietary-options`),
        $fetch<TagsResponse>(`${baseUrl}/api/search/tags`)
      ])

      // Process categories
      const categoriesData = Normalization.arrayValue<unknown>(
        categoriesResponse.data.categories
      ).map(normalizeCategory)
      categories.value = categoriesData
        .filter((category) => category.type !== 'dietary')
        .map((category): AppCategory => ({
          ...category,
          isSelected: false
        }))

      // Process dietary options
      dietaryOptions.value = Normalization.arrayValue<unknown>(
        dietaryResponse.data.dietaryOptions
      ).map(normalizeDietaryOption)

      // Process tags (grouped by type)
      tags.value = Normalization.assocArray(tagsResponse.data?.tags, {})
      tagGroups.value = Normalization.stringList(tagsResponse.data?.groups, [])
    } catch (err: unknown) {
      failure.value = serializeAppProblem(AppProblemModel.normalize(err, { context: 'app data' }))

      categories.value = []
      dietaryOptions.value = []
      tags.value = {}
      tagGroups.value = []
    } finally {
      loading.value = false
    }
  })()

  return globalFetchPromise
}

export const useAppData = () => {
  const t = usePresentationTranslator()
  const categories = useState<AppCategory[]>('app-categories', () => [])
  const dietaryOptions = useState<DietaryOption[]>('app-dietary-options', () => [])
  const tags = useState<Record<string, unknown>>('app-tags', () => ({}))
  const tagGroups = useState<string[]>('app-tag-groups', () => [])
  const loading = useState<boolean>('app-loading', () => false)
  const failure = useState<AppProblemModel | null>('app-error', () => null)
  const error = computed(() =>
    failure.value !== null
      ? resolveProblemMessage(failure.value, t, t('common.loading.unableToLoadAppData'))
      : null
  )

  const fetchAppData = async () => {
    return fetchAppDataGlobal(categories, dietaryOptions, tags, tagGroups, loading, failure)
  }

  return {
    categories,
    dietaryOptions,
    tags,
    tagGroups,
    loading,
    error,
    fetchAppData
  }
}
